import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { AddPurchasePaymentDialog } from 'src/dialogs/AddPurchasePaymentDialog';
import {
  ConvertCurrencyDialog,
  ConvertCurrencyForm,
} from 'src/dialogs/ConvertCurrency/ConvertCurrencyDialog';
import { EditPurchasePaymentDialog } from 'src/dialogs/EditPurchasePaymentDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { PaymentDetailsTable } from 'src/modals/common/Purchase/PaymentDetailsTable/PaymentDetailsTable';
import { PurchaseOrderFormDataType } from 'src/modals/MultiplePurchaseWizard/MultiplePurchaseWizard';
import { IconsFill, PlusIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { posChangedField } from 'src/utils/posFieldUtils';
import {
  PosFieldArrayOfPurchasePayment,
  PurchasePayment,
  PurchasePaymentStatus,
} from 'src/WebApiController';

export const usePaymentDetailsInput = ({
  index,
  currency,
  payments,
}: {
  index: number;
  currency: string;
  payments: PosFieldArrayOfPurchasePayment | null;
}) => {
  const { loginContext } = useAppContext();
  const currentUserId = loginContext?.user?.userId!;
  const { getValues, setValue, formState } =
    useFormContext<PurchaseOrderFormDataType>();

  const [currentPayment, setCurrentPayment] = useState<PurchasePayment>();

  const editPaymentDialog = useBasicDialog();
  const convertCurrencyDialog = useBasicDialog();
  const addPaymentDialog = useBasicDialog();

  const onSetPaymentPaid = useCallback(
    ({ paymentId }: PurchasePayment) => {
      const index = payments?.value?.findIndex(
        (p) => p.paymentId === paymentId
      );
      if (index != null && index >= 0) {
        const newPaymentDate = new Date().toISOString();
        if (newPaymentDate > payments!.value![index].paymentDueDate) {
          setValue(
            `purchaseOrders.${index}.payments.value.${index}.paymentStatus`,
            PurchasePaymentStatus.RefundNeeded
          );
        } else {
          setValue(
            `purchaseOrders.${index}.payments.value.${index}.paymentStatus`,
            PurchasePaymentStatus.Paid
          );
        }

        setValue(
          `purchaseOrders.${index}.payments.value.${index}.paymentDate`,
          newPaymentDate
        );
        setValue(`purchaseOrders.${index}.payments.hasChanged`, true);
      }
    },
    [payments, setValue]
  );

  const onAddNewPayment = useCallback(() => {
    addPaymentDialog.launchDialog();
  }, [addPaymentDialog]);

  const onAddNewPaymentSave = useCallback(
    (payments: PurchasePayment[]) => {
      const existingPayments = getValues(`purchaseOrders.${index}.payments`);
      if (existingPayments == null) {
        setValue(`purchaseOrders.${index}.payments`, posChangedField(payments));
      } else {
        setValue(
          `purchaseOrders.${index}.payments`,
          posChangedField([...existingPayments!.value!, ...payments])
        );
      }
      addPaymentDialog.closeDialog();
    },
    [setValue, getValues, addPaymentDialog]
  );

  const onAddNewPaymentCancel = useCallback(() => {
    addPaymentDialog.closeDialog();
  }, [addPaymentDialog]);

  const onEditPayment = useCallback(
    (payment: PurchasePayment) => {
      setCurrentPayment(payment);

      editPaymentDialog.launchDialog();
    },
    [editPaymentDialog]
  );

  const onRemovePayment = useCallback(
    ({ paymentId }: PurchasePayment) => {
      const newPayments = payments?.value?.filter(
        (p) => p.paymentId !== paymentId
      );
      if (newPayments && newPayments?.length !== payments?.value?.length) {
        setValue(
          `purchaseOrders.${index}.payments`,
          posChangedField(newPayments)
        );
      }

      editPaymentDialog.closeDialog();
    },
    [payments?.value, editPaymentDialog, setValue]
  );

  const onEditPaymentSave = useCallback(
    (payment: PurchasePayment) => {
      const existingPayment = payments?.value?.find(
        (p) => p.paymentId === payment.paymentId
      );
      if (existingPayment) {
        payment.paymentId = existingPayment.paymentId;
        Object.assign(existingPayment, payment);

        setValue(
          `purchaseOrders.${index}.payments`,
          posChangedField(payments!.value)
        );
      }

      editPaymentDialog.closeDialog();
    },
    [payments, editPaymentDialog, setValue]
  );
  const onEditPaymentCancel = useCallback(() => {
    setCurrentPayment(undefined);
    editPaymentDialog.closeDialog();
  }, [editPaymentDialog]);

  const onConvertCurrency = useCallback(
    (payment: PurchasePayment) => {
      setCurrentPayment(payment);
      convertCurrencyDialog.launchDialog();
    },
    [convertCurrencyDialog]
  );

  const onSaveConvertCurrency = useCallback(
    (formValues: ConvertCurrencyForm) => {
      if (currentPayment && payments) {
        const updatedPayment = Object.assign({}, currentPayment, formValues);
        const filteredPayments = (payments.value ?? []).map((payment) =>
          payment.paymentId === currentPayment.paymentId
            ? updatedPayment
            : payment
        );
        setValue(
          `purchaseOrders.${index}.payments`,
          posChangedField(filteredPayments)
        );
      }
      convertCurrencyDialog.closeDialog();
    },
    [convertCurrencyDialog, currentPayment, payments, setValue]
  );

  const onCancelConvertCurrency = useCallback(() => {
    convertCurrencyDialog.closeDialog();
    setCurrentPayment(undefined);
  }, [convertCurrencyDialog]);

  const addPurchasePaymentDialog = addPaymentDialog.dialogProps.isOpen && (
    <AddPurchasePaymentDialog
      {...addPaymentDialog.dialogProps}
      defaultCurrencyCode={currency}
      onSave={onAddNewPaymentSave}
      onCancel={onAddNewPaymentCancel}
    />
  );

  const editPurchasePaymentDialog = editPaymentDialog.dialogProps.isOpen &&
    currentPayment && (
      <EditPurchasePaymentDialog
        {...editPaymentDialog.dialogProps}
        onSave={onEditPaymentSave}
        onCancel={onEditPaymentCancel}
        payment={currentPayment}
      />
    );

  const convertCurrencyPaymentDialog = convertCurrencyDialog.dialogProps
    .isOpen &&
    currentPayment && (
      <ConvertCurrencyDialog
        {...convertCurrencyDialog.dialogProps}
        onSave={onSaveConvertCurrency}
        onCancel={onCancelConvertCurrency}
        payment={currentPayment}
        currentUserId={currentUserId}
      />
    );

  const paymentDetailsTable = (
    <>
      <PaymentDetailsTable
        currencyCode={currency}
        payments={payments?.value}
        onSetAsPaid={onSetPaymentPaid}
        onEditPayment={onEditPayment}
        onRemovePayment={onRemovePayment}
        onConvertCurrency={onConvertCurrency}
      />
      <Button variant={'link'} onClick={onAddNewPayment}>
        <PlusIcon size={vars.iconSize.s} fill={IconsFill.currentColor} />
        <Content id={ContentId.AddLineItem} />
      </Button>
    </>
  );

  return {
    editPurchasePaymentDialog,
    paymentDetailsTable,
    addPurchasePaymentDialog,
    convertCurrencyPaymentDialog,
  };
};
